// src/features/dashboard/components/ServiceSummary.tsx
import { GlassCard } from '../../../app/components/ui/GlassCard';
import { StatCard } from './StatCard';
import { StatCardSkeleton } from './ui/Skeleton';
import type { StatCardData } from '../types/dashboard.types';

interface ServiceSummaryProps {
  stats: StatCardData[];
  isInitialLoading: boolean;
}

/**
 * Dedicated services widget for the dashboard.
 *
 * Scope, intentionally narrow: renders only the existing `active-services`
 * stat via the existing `StatCard` primitive -- no expiring/expired
 * metrics, no derived counts, no new fields on `StatCardData`.
 *
 * Extensible by construction: `stats` is passed through untouched (same
 * `StatCardData[]` contract everywhere else on the dashboard), so once a
 * real expiring/expired data source exists, additional `StatCard`s can be
 * appended here without changing this component's props or the data
 * contract. Nothing about today's single-metric render forecloses that.
 *
 * Dashboard V3 Phase 2: sits beside QuickActions in the "Business
 * Operations" section, so it gets its own small heading (matching
 * QuickActions' internal title treatment) instead of rendering as a bare,
 * unlabeled card.
 *
 * Phase 6 -- Layout Correction: was a bare `<div>` with no surface of its
 * own, so next to QuickActions' full `GlassCard` it read as an
 * under-weight, half-finished panel with a lot of dead space beneath a
 * single stat. Now wrapped in the same `GlassCard` shell QuickActions
 * uses (`h-full` + `.vania-ops-panel` floor height), with the same shared
 * `.vania-panel-title` heading treatment, and the single `StatCard`
 * centered in the remaining space instead of pinned to the top -- so the
 * two panels read as a genuinely matched pair. Still exactly one real
 * metric, still renders nothing when there's no data and we're not
 * loading, same as before.
 */
export function ServiceSummary({ stats, isInitialLoading }: ServiceSummaryProps) {
  const activeServicesStat = stats.find((stat) => stat.id === 'active-services') ?? null;

  if (!isInitialLoading && !activeServicesStat) {
    return null;
  }

  return (
    <GlassCard className="vania-ops-panel flex h-full flex-col gap-4" data-animate="fade-in">
      <h3 className="vania-panel-title px-1">خلاصه سرویس‌ها</h3>
      <div className="flex flex-1 flex-col justify-center">
        {isInitialLoading ? <StatCardSkeleton /> : <StatCard data={activeServicesStat as StatCardData} />}
      </div>
    </GlassCard>
  );
}
